import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";

/** Put text on the clipboard: async API first, then the legacy execCommand route. Returns false if the browser refused both. */
export async function writeClipboard(text: string): Promise<boolean> {
  try { if (window.isSecureContext && navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; } } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.top = "0"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.focus(); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

type Sheet = { title: string; text: string };
let listener: ((s: Sheet) => void) | null = null;

/** Copy `text`; if the browser blocks the clipboard, open the sheet in a panel so it can be copied by hand. */
export async function copyOrShow(text: string, done: string, title = "Pick sheet") {
  if (await writeClipboard(text)) { toast.success(done); return; }
  toast.message("Your browser blocked the clipboard — the sheet is open below, copy it from there");
  listener?.({ title, text });
}

/** Mounted once in the admin shell; shows the sheet when the clipboard was refused. */
export function SheetFallbackHost() {
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { listener = setSheet; return () => { listener = null; }; }, []);
  const copyNow = async () => { if (sheet && (await writeClipboard(sheet.text))) { toast.success("Copied"); setSheet(null); } else { ref.current?.select(); toast.message("Press Ctrl+C / ⌘C to copy the selected text"); } };
  return (
    <Dialog open={!!sheet} onOpenChange={(o) => !o && setSheet(null)}>
      <DialogContent className="max-w-3xl rounded-none" data-testid="sheet-fallback">
        <DialogHeader><DialogTitle className="font-serif text-xl font-normal">{sheet?.title}</DialogTitle><DialogDescription>The browser would not write to the clipboard automatically. Copy the sheet from here.</DialogDescription></DialogHeader>
        <textarea ref={ref} readOnly value={sheet?.text || ""} onFocus={(e) => e.currentTarget.select()} className="h-72 w-full border border-border bg-neutral-50 p-2 font-mono text-[11px] leading-snug" data-testid="sheet-fallback-text" />
        <div className="flex gap-2"><Button size="sm" className="h-8 rounded-none text-xs" onClick={copyNow} data-testid="sheet-fallback-copy">Copy</Button><Button size="sm" variant="ghost" className="h-8 rounded-none text-xs" onClick={() => setSheet(null)} data-testid="sheet-fallback-close">Close</Button></div>
      </DialogContent>
    </Dialog>
  );
}
